import {
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  FocusEvent,
} from "react";
import classNames from "classnames";
import "./AppTagInput.css";

interface AppTagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  small?: boolean;
  expanded?: boolean;
}

export default function AppTagInput({
  value,
  onChange,
  disabled,
  small,
  expanded,
}: AppTagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState("60px");

  useEffect(() => {
    if (expanded && wrapperRef.current) {
      const fullHeight = wrapperRef.current.scrollHeight;
      setHeight(`${fullHeight}px`);
    } else {
      setHeight("60px");
    }
  }, [expanded, value]);

  const addTag = () => {
    const newTag = inputValue.trim();
    if (newTag && !value.includes(newTag)) {
      onChange([...value, newTag]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const trimmedValue = inputValue.trim();
    if ((e.key === "Enter" || e.key === " ") && trimmedValue) {
      e.preventDefault();
      addTag();
    }

    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      !inputValue &&
      value.length > 0
    ) {
      const newTags = value.slice(0, -1);
      onChange(newTags);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (inputValue.trim()) {
      addTag();
    }
  };

  const handleRemove = (indexToRemove: number) => {
    const newTags = value.filter((_, index) => index !== indexToRemove);
    onChange(newTags);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  return (
    <div
      ref={wrapperRef}
      className={classNames("app-tag-input", {
        "app-tag-input--small": small,
      })}
      style={
        small
          ? {
              height,
              transition: "height 0.2s ease",
              overflowY: expanded ? "auto" : "hidden",
              maxHeight: "fit-content",
            }
          : undefined
      }
    >
      {value.map((keyword, index) => (
        <div key={index} className="tag">
          {keyword}
          <button
            type="button"
            className="remove-btn"
            onClick={() => handleRemove(index)}
            aria-label="Remove"
          >
            ×
          </button>
        </div>
      ))}
      <input
        type="text"
        className="input-field"
        placeholder="Enter the keyword"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
      />
    </div>
  );
}
